'use client';

import { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Badge,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Drawer,
    Divider,
    LinearProgress,
    IconButton,
    Chip,
    Paper,
    Grid,
    CircularProgress,
    Skeleton,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MoneyIcon from '@mui/icons-material/Money';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'sonner';
import { useAuthStore } from '../../../store/useAuthStore';
import { useNotificationStore } from '../../../store/useNotificationStore';
import apiClient from '../../../services/api-client';
import { SmartphoneIcon } from 'lucide-react';
import PaymentDetailSheet from '../components/PaymentDetailSheet';
import { AddPaymentDialog } from '../components/AddPaymentDialog copy';

// ---- statut Config ----
const statutConfig = {
    'EMISE': {
        label: 'Émise',
        color: '#1565c0',
        bgColor: 'rgba(21, 101, 192, 0.08)',
        icon: <AccessTimeIcon sx={{ fontSize: 14 }} />,
    },
    'PAYEE': {
        label: 'Payée',
        color: '#2e7d32',
        bgColor: 'rgba(46, 125, 50, 0.08)',
        icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
    },
    'ANNULEE': {
        label: 'Annulée',
        color: '#757575',
        bgColor: 'rgba(117, 117, 117, 0.08)',
        icon: <CancelIcon sx={{ fontSize: 14 }} />,
    },
    'EN_RETARD': {
        label: 'En retard',
        color: '#c62828',
        bgColor: 'rgba(198, 40, 40, 0.08)',
        icon: <WarningAmberIcon sx={{ fontSize: 14 }} />,
    },
    'PARTIELLEMENT_PAYEE': {
        label: 'Partielle',
        color: '#ef6c00',
        bgColor: 'rgba(239, 108, 0, 0.08)',
        icon: <AttachMoneyIcon sx={{ fontSize: 14 }} />,
    },
};

const methodConfig = {
    'ESPECES': { label: 'Espèces', icon: <MoneyIcon sx={{ fontSize: 18 }} />, color: '#2e7d32' },
    'CARTE_BANCAIRE': { label: 'Carte bancaire', icon: <CreditCardIcon sx={{ fontSize: 18 }} />, color: '#1565c0' },
    'VIREMENT': { label: 'Virement', icon: <AccountBalanceIcon sx={{ fontSize: 18 }} />, color: '#6a1b9a' },
    'CHEQUE': { label: 'Chèque', icon: <ApartmentIcon sx={{ fontSize: 18 }} />, color: '#ef6c00' },
    'MOBILE_MONEY': { label: 'Mobile', icon: <SmartphoneIcon sx={{ fontSize: 18 }} />, color: '#388e3c' },
};

// ---- KPI Card ----
function PaymentKPI({ title, value, subtitle, icon, color, bgColor }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
        >
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    p: 2.5,
                    border: `1px solid ${bgColor}`,
                    backgroundColor: '#faf7f2',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
                }}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: bgColor,
                        color,
                        mb: 1.5,
                    }}
                >
                    {icon}
                </Box>
                <Typography variant="body2" sx={{ color: '#666', fontFamily: "'DM Sans', sans-serif", mb: 0.5 }}>
                    {title}
                </Typography>
                <Typography
                    variant="h5"
                    sx={{ color: '#1a1a2e', fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, lineHeight: 1.2 }}
                >
                    {value}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', fontFamily: "'DM Sans', sans-serif" }}>
                    {subtitle}
                </Typography>
            </Paper>
        </motion.div>
    );
}

// ---- Invoice Row ----
function InvoicePaymentRow({ invoice, onSelect, onAddPayment }) {
    const totalPaid = invoice.montantPaye || 0;
    const total = invoice.montantTotal || 0;
    const remaining = invoice.resteAPayer || 0;
    const progressPercent = total > 0 ? Math.min(100, (totalPaid / total) * 100) : 0;
    const isPayable = invoice.statut !== 'ANNULÉE' && invoice.statut !== 'PAYÉE';
    const statut = statutConfig[invoice.statut];

    return (
        <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
        >
            <Box
                onClick={() => onSelect(invoice)}
                sx={{
                    px: 2.5,
                    py: 2,
                    borderBottom: '1px solid rgba(201, 168, 76, 0.05)',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.6)' },
                    '&:hover .add-btn': { opacity: 1 },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography
                            variant="caption"
                            sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#c9a84c', letterSpacing: 0.5 }}
                        >
                            {invoice.invoiceNumber}
                        </Typography>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                backgroundColor: statut.bgColor,
                                color: statut.color,
                                fontSize: 10,
                                fontWeight: 600,
                            }}
                        >
                            {statut.icon}
                            {statut.label}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isPayable && (
                            <Button
                                className="add-btn"
                                size="small"
                                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                                sx={{
                                    opacity: 0,
                                    transition: 'opacity 0.15s',
                                    color: '#c9a84c',
                                    fontSize: 11,
                                    minWidth: 0,
                                    py: 0.25,
                                    textTransform: 'none',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAddPayment(invoice);
                                }}
                            >
                                Paiement
                            </Button>
                        )}
                        <ChevronRightIcon sx={{ fontSize: 16, color: '#ccc' }} />
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#1a1a2e' }}>
                        {invoice.clientName}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: '#1a1a2e', fontFamily: "'Cormorant Garamond', serif", fontSize: 16 }}
                    >
                        {total.toLocaleString('fr-FR')} €
                    </Typography>
                </Box>

                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#888', fontSize: 11 }}>
                            Payé : {totalPaid.toLocaleString('fr-FR')} €
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#888', fontSize: 11 }}>
                            Reste : {remaining.toLocaleString('fr-FR')} €
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            overflow: 'hidden',
                            backgroundColor: 'rgba(201, 168, 76, 0.1)',
                        }}
                    >
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${progressPercent}%` }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}
                            style={{
                                height: '100%',
                                borderRadius: 4,
                                background:
                                    progressPercent >= 100
                                        ? 'linear-gradient(135deg, #1b5e20, #2e7d32)'
                                        : progressPercent > 0
                                            ? 'linear-gradient(135deg, #b08d3a, #c9a84c)'
                                            : 'transparent',
                            }}
                        />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#aaa', fontSize: 10, display: 'block', textAlign: 'right', mt: 0.5 }}>
                        {progressPercent.toFixed(0)}% payé
                    </Typography>
                </Box>
            </Box>
        </motion.div>
    );
}



// ---- Main Component ----
export default function PaiementDashboard() {
    const [invoices, setInvoices] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('ALL');
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [addPaymentOpen, setAddPaymentOpen] = useState(false);
    const [addPaymentInvoice, setAddPaymentInvoice] = useState(null);
    const addNotification = useNotificationStore((s) => s.addNotification);

    const fetchInvoices = useCallback(async () => {
        try {
            const res = await apiClient.get('/api/invoices');
            setInvoices(res.data.content || []);
        } catch (error) {
            console.error("Erreur lors de la récupération des factures:", error);
        }
    }, []);

    const fetchPayments = useCallback(async () => {
        try {
            const res = await apiClient.get('/api/paiements');
            setPayments(res.data.content || []);
        } catch (error) {
            console.error("Erreur lors de la récupération des paiements:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        Promise.all([fetchInvoices(), fetchPayments()]);
    }, [fetchInvoices, fetchPayments]);

    const handleSelectInvoice = (invoice) => {
        setSelectedInvoice(invoice);
        setDetailOpen(true);
    };

    const handleAddPayment = (invoice) => {
        setAddPaymentInvoice(invoice);
        setAddPaymentOpen(true);
    };

    const handlePaymentSuccess = () => {
        fetchInvoices();
        fetchPayments();
        addNotification({
            type: 'PAYMENT_RECEIVED',
            title: '💰 Paiement enregistré',
            message: `Nouveau versement sur la facture ${addPaymentInvoice?.invoiceNumber}`,
            entityId: addPaymentInvoice?.id,
            entityType: 'INVOICE',
        });
    };

    const getPaymentsForInvoice = (invoiceId) => payments.filter((p) => p.invoiceId === invoiceId);

    const filteredInvoices = filter === 'ALL' ? invoices : invoices.filter((inv) => inv.statut === filter);

    const totalRevenue = invoices.reduce((sum, inv) => sum + inv.totalWithTax, 0);
    const totalPaid = invoices.reduce((sum, inv) => sum + (inv.totalPaid || 0), 0);
    const totalRemaining = invoices.reduce((sum, inv) => sum + (inv.remainingAmount || 0), 0);
    const partiallyPaidCount = invoices.filter((inv) => inv.statut === 'PARTIELLEMENT_PAYEE').length;
    const fullyPaidCount = invoices.filter((inv) => inv.statut === 'PAYEE').length;
    const statutCounts = invoices.reduce((acc, inv) => {
        acc[inv.statut] = (acc[inv.statut] || 0) + 1;
        return acc;
    }, {});

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Grid container spacing={2}>
                    {[1, 2, 3, 4].map((i) => (
                        <Grid item xs={12} sm={6} lg={3} key={i}>
                            <Skeleton variant="rounded" height={112} sx={{ borderRadius: 3, bgcolor: '#f0ebe0' }} />
                        </Grid>
                    ))}
                </Grid>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Skeleton key={i} variant="rounded" height={96} sx={{ borderRadius: 3, bgcolor: '#f0ebe0' }} />
                    ))}
                </Box>
            </Box>
        );
    }

    const filters = ['ALL', 'EMISE', 'PARTIELLEMENT_PAYEE', 'PAYEE', 'EN_RETARD', 'ANNULEE'];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* KPI Cards */}
            <Grid container spacing={2}>
                {[
                    {
                        title: 'Total Facturé',
                        value: `${totalRevenue.toLocaleString('fr-FR')} €`,
                        subtitle: `${invoices.length} factures`,
                        icon: <ReceiptIcon sx={{ fontSize: 20 }} />,
                        color: '#1a1a2e',
                        bgColor: 'rgba(26, 26, 46, 0.06)',
                    },
                    {
                        title: 'Total Encaissé',
                        value: `${totalPaid.toLocaleString('fr-FR')} €`,
                        subtitle: `${fullyPaidCount} factures soldées`,
                        icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
                        color: '#2e7d32',
                        bgColor: 'rgba(46, 125, 50, 0.08)',
                    },
                    {
                        title: 'Reste à Encaisser',
                        value: `${totalRemaining.toLocaleString('fr-FR')} €`,
                        subtitle: `${partiallyPaidCount} paiements partiels`,
                        icon: <AccessTimeIcon sx={{ fontSize: 20 }} />,
                        color: '#ef6c00',
                        bgColor: 'rgba(239, 108, 0, 0.08)',
                    },
                    {
                        title: 'Versements',
                        value: payments.length,
                        subtitle: partiallyPaidCount > 0 ? 'Paiements échelonnés actifs' : 'Aucun échelonné',
                        icon: <AccountBalanceWalletIcon sx={{ fontSize: 20 }} />,
                        color: '#c9a84c',
                        bgColor: 'rgba(201, 168, 76, 0.08)',
                    },
                ].map((kpi) => (
                    <Grid item xs={12} sm={6} lg={3} key={kpi.title}>
                        <PaymentKPI {...kpi} />
                    </Grid>
                ))}
            </Grid>

            {/* Invoices List */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(201, 168, 76, 0.12)',
                    backgroundColor: '#faf7f2',
                    overflow: 'hidden',
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        borderBottom: '1px solid rgba(201, 168, 76, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography variant="h6" sx={{ fontFamily: "'Cormorant Garamond', serif", color: '#1a1a2e' }}>
                            Paiements des Factures
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999', fontFamily: "'DM Sans', sans-serif" }}>
                            Gérez les paiements échelonnés et suivez les encaissements
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {filters.map((statut) => (
                            <Box
                                key={statut}
                                onClick={() => setFilter(statut)}
                                sx={{
                                    px: 1.5,
                                    py: 0.75,
                                    borderRadius: 2,
                                    fontSize: 12,
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    backgroundColor: filter === statut ? 'rgba(201, 168, 76, 0.15)' : 'transparent',
                                    color: filter === statut ? '#c9a84c' : '#888',
                                    border: filter === statut ? '1px solid rgba(201, 168, 76, 0.3)' : '1px solid transparent',
                                    '&:hover': { backgroundColor: 'rgba(201, 168, 76, 0.08)', color: '#c9a84c' },
                                }}
                            >
                                {statut === 'ALL' ? 'Toutes' : statutConfig[statut].label}
                                {statut !== 'ALL' && statutCounts[statut] ? (
                                    <Box component="span" sx={{ ml: 0.5, opacity: 0.6 }}>({statutCounts[statut]})</Box>
                                ) : null}
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Table Header */}
                <Box
                    sx={{
                        px: 2.5,
                        py: 1.25,
                        display: 'grid',
                        gridTemplateColumns: '3fr 2fr 3fr 2fr 2fr',
                        gap: 1.5,
                        borderBottom: '1px solid rgba(201, 168, 76, 0.08)',
                        backgroundColor: 'rgba(201, 168, 76, 0.03)',
                    }}
                >
                    {['Facture / Client', 'Total TTC', 'Progression', 'Statut', 'Échéance'].map((col) => (
                        <Typography key={col} variant="caption" sx={{ color: '#999', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, fontSize: 10 }}>
                            {col}
                        </Typography>
                    ))}
                </Box>

                {/* Rows */}
                <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
                    {filteredInvoices.map((invoice) => (
                        <InvoicePaymentRow
                            key={invoice.id}
                            invoice={invoice}
                            onSelect={handleSelectInvoice}
                            onAddPayment={handleAddPayment}
                        />
                    ))}
                    {filteredInvoices.length === 0 && (
                        <Box sx={{ py: 6, textAlign: 'center', color: '#aaa' }}>
                            <ReceiptIcon sx={{ fontSize: 32, opacity: 0.3, mb: 1 }} />
                            <Typography variant="body2">Aucune facture trouvée</Typography>
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Recent Payments */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(201, 168, 76, 0.12)',
                    backgroundColor: '#faf7f2',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid rgba(201, 168, 76, 0.08)', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalanceWalletIcon sx={{ color: '#c9a84c', fontSize: 20 }} />
                    <Typography variant="h6" sx={{ fontFamily: "'Cormorant Garamond', serif", color: '#1a1a2e', fontSize: 18 }}>
                        Derniers Versements
                    </Typography>
                </Box>
                <Box sx={{ maxHeight: 256, overflowY: 'auto' }}>
                    {[...payments]
                        .sort((a, b) => new Date(b.datePaiement).getTime() - new Date(a.datePaiement).getTime())
                        .slice(0, 10)
                        .map((payment, idx) => {
                            const method = methodConfig[payment.modePaiement];
                            const inv = invoices.find((i) => i.id === payment.factureId);
                            return (
                                <motion.div
                                    key={payment.id}
                                    initial={{ opacity: 0, y: 5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.03 }}
                                >
                                    <Box
                                        sx={{
                                            px: 2.5,
                                            py: 1.5,
                                            borderBottom: '1px solid rgba(201, 168, 76, 0.05)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            transition: 'background 0.15s',
                                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.6)' },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: 1.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: `${method.color}12`,
                                                    color: method.color,
                                                }}
                                            >
                                                {method.icon}
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a1a2e' }}>
                                                    {payment.montant.toLocaleString('fr-FR')} €
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#999', fontSize: 11 }}>
                                                    {inv?.invoiceNumber || payment.factureId} • {method.label}
                                                    {payment.codePaiement && ` • ${payment.codePaiement}`}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                                                {new Date(payment.paymentDate).toLocaleDateString('fr-FR')}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#aaa', fontSize: 10 }}>
                                                par {payment.createdBy.split(' ')[0]}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </motion.div>
                            );
                        })}
                    {payments.length === 0 && (
                        <Box sx={{ py: 4, textAlign: 'center', color: '#aaa' }}>
                            <AccountBalanceWalletIcon sx={{ fontSize: 32, opacity: 0.3, mb: 1 }} />
                            <Typography variant="body2">Aucun versement enregistré</Typography>
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Detail Drawer */}
            <PaymentDetailSheet
                invoice={selectedInvoice}
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                onAddPayment={handleAddPayment}
            />

            {/* Add Payment Dialog */}
            <AddPaymentDialog
                invoice={addPaymentInvoice}
                open={addPaymentOpen}
                onOpenChange={setAddPaymentOpen}
                onSuccess={handlePaymentSuccess}
            />
        </Box>
    );
}